import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CaretLeft, Envelope, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getEnrollmentRecord } from "@/lib/enrollment";
import {
  formatChildName,
  formatEnrollmentDate,
  formatPersonName,
  parentStatusLabel,
  TERMINAL_STAGES,
} from "@/lib/enrollmentTypes";
import { isSupabaseConfigured } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import StageBadge from "@/components/enrollment/StageBadge";
import ParentTimeline from "@/components/enrollment/ParentTimeline";
import DocumentChecklist from "@/components/enrollment/DocumentChecklist";

export default function ParentEnrollmentStatus() {
  const { id } = useParams();
  const { profile } = useAuth();
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await getEnrollmentRecord(id);
        setRec(r);
      } catch (err) {
        toast.error(err.message || "Could not load your application");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!rec) {
    return (
      <div className="py-16 text-center text-sm text-neutral-500">
        Application not found.
      </div>
    );
  }

  const terminal = TERMINAL_STAGES.has(rec.stage);

  return (
    <div data-testid="parent-enrollment-status" data-stage={rec.stage} className="space-y-5">
      <Link
        to="/parent/enrollment"
        data-testid="enr-status-back"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
      >
        <CaretLeft size={16} weight="bold" />
        Back
      </Link>

      <header className="rounded-2xl border border-neutral-200 bg-white p-5">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Status
        </p>
        <h1 className="mt-1 font-heading text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">
          {parentStatusLabel(rec.stage)}
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          For {formatChildName(rec)} · Updated {formatEnrollmentDate(rec.stage_changed_at)}
        </p>
        <div className="mt-3">
          <StageBadge stage={rec.stage} />
        </div>
      </header>

      {!terminal && rec.stage !== "enrolled" && (
        <section className="rounded-2xl border border-neutral-200 bg-white p-5">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Progress
          </p>
          <ParentTimeline currentStage={rec.stage} />
        </section>
      )}

      {(rec.stage === "offer" || rec.stage === "accepted") && (
        <div
          data-testid="parent-offer-banner"
          className="rounded-2xl border border-primary/30 bg-primary/5 p-5"
        >
          <p className="font-heading text-base font-semibold text-neutral-900">
            Your spot is ready!
          </p>
          <p className="mt-1 text-sm text-neutral-700">
            Review the offer and complete any requested enrollment documents
            in HoneyAcorn. Your center will confirm the remaining next steps.
          </p>
          {rec.offer_expires_at && (
            <p className="mt-2 text-xs font-semibold text-primary-hover">
              Please review by {formatEnrollmentDate(rec.offer_expires_at)}.
            </p>
          )}
        </div>
      )}

      {rec.stage === "enrollment_pending" && (
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
          <p className="font-heading text-base font-semibold text-neutral-900">
            We’re completing enrollment
          </p>
          <p className="mt-1 text-sm text-neutral-700">
            Your center is preparing the final details. We’ll share anything
            else that needs your attention in HoneyAcorn.
          </p>
        </div>
      )}

      <section className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-3">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Your inquiry
        </p>
        <dl className="grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="Family" value={formatPersonName(rec)} />
          <Row label="Child" value={formatChildName(rec)} />
          <Row
            label="Preferred start"
            value={formatEnrollmentDate(rec.preferred_start_date)}
          />
          <Row
            label="Preferred classroom"
            value={rec.classroom?.name || rec.preferred_age_group || "No preference"}
          />
        </dl>
        {rec.parent_notes && (
          <p className="mt-3 rounded-xl bg-neutral-50 px-3 py-2 text-sm text-neutral-700">
            <span className="font-semibold">Notes · </span>
            {rec.parent_notes}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3 pt-2 text-xs text-neutral-500">
          <span className="inline-flex items-center gap-1">
            <Envelope size={12} weight="duotone" /> {rec.parent_email}
          </span>
          {rec.parent_phone && (
            <span className="inline-flex items-center gap-1">
              <Phone size={12} weight="duotone" /> {rec.parent_phone}
            </span>
          )}
        </div>
      </section>

      {isSupabaseConfigured && (
        <DocumentChecklist
          testId="parent-document-checklist"
          enrollment={rec}
          organizationId={rec.organization_id}
          role="parent"
          canUpload
          uploadedBy={profile?.id}
        />
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-36 flex-shrink-0 text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">
        {label}
      </dt>
      <dd className="text-sm text-neutral-800">{value}</dd>
    </div>
  );
}
